import { DependencyInfoOptions } from '../../../../../../../../shared/reports';
import { selectOptionFactory } from '../../../../../../../utils';
import { ReportOptionDetails } from '../../../utils';
import { licenseIncludeOptions } from '../../constants';
import type { PartialReportConfig } from '../../../../../../../../shared/reports/types';
import type { SelectOption } from '../../../../../../../types';
import type { IncludeFilterOption } from '../../types';

type SectionKey = keyof NonNullable<PartialReportConfig['sections']>;
type ToggleKey = keyof NonNullable<PartialReportConfig['toggles']>;
type DependencyDataKey = keyof NonNullable<PartialReportConfig['dependencyData']>;

const sectionToIncludeOption: Record<SectionKey, IncludeFilterOption> = {
  dependencySummary: 'includeDependencySummary',
  firstPartyNoticeFiles: 'includeNoticeFiles',
  directDependencies: 'includeDirectDependencies',
  deepDependencies: 'includeDeepDependencies',
  snippetDependencies: 'includeSnippetDependencies',
  projectDeclaredLicenses: 'includeProjectLicense',
  firstPartyLicenses: 'includeLicenseScan',
  copyrightList: 'includeCopyrightList',
  fossaDependencies: 'includeFOSSADependencies',
  openVulnerabilities: 'includeOpenVulnerabilities',
  closedVulnerabilities: 'includeClosedVulnerabilities',
  supportStatus: 'includeSupportStatus',
  licenseFileMatches: 'includeFileMatches',
  concludedLicenses: 'includeConcludedLicenses',
  nonLicenseCopyrights: 'includeNonLicenseCopyrights',
};

// The as is necessary to cast the result of Object.entries to the correct type, otherwise it treats all keys as strings.
const sectionToIncludeOptionEntries: Array<[SectionKey, IncludeFilterOption]> = Object.entries(
  sectionToIncludeOption,
) as Array<[SectionKey, IncludeFilterOption]>;

const toggleToIncludeOption: Record<ToggleKey, IncludeFilterOption> = {
  useHashAndVersionData: 'includeHashAndVersionData',
  excludeUnknownDependencies: 'excludeUnknownDependencies',
};

// The as is necessary to cast the result of Object.entries to the correct type, otherwise it treats all keys as strings.
const toggleToIncludeOptionEntries: Array<[ToggleKey, IncludeFilterOption]> = Object.entries(
  toggleToIncludeOption,
) as Array<[ToggleKey, IncludeFilterOption]>;

const dependencyDataToInfoOption: Partial<Record<DependencyDataKey, keyof typeof DependencyInfoOptions>> = {
  projects: 'Project',
  authors: 'Authors',
  description: 'Description',
  homepage: 'ProjectUrl',
  packageManager: 'Source',
  downloadUrl: 'PackageDownloadUrl',
  concludedLicenses: 'ConcludedLicense',
  declaredLicenses: 'License',
  discoveredLicenses: 'OtherLicenses',
  copyrights: 'Copyrights',
  licenseUrl: 'LicenseFileURL',
  issueResolutionNotes: 'IssueNotes',
  dependencyPaths: 'DependencyPaths',
  filePaths: 'FilePath',
  fullLicenseText: 'FullTextLicense',
  licenseTextAppendix: 'LicenseTextAppendix',
  licenseHeaders: 'CustomTextLicense',
};

const dependencyDataToInfoOptionEntries: Array<[DependencyDataKey, keyof typeof DependencyInfoOptions]> =
  // The as is necessary to cast the result of Object.entries to the correct type, otherwise it treats all keys as strings.
  Object.entries(dependencyDataToInfoOption) as Array<[DependencyDataKey, keyof typeof DependencyInfoOptions]>;

const dependencyDataToIncludeOption: Partial<Record<DependencyDataKey, IncludeFilterOption>> = {
  licenseHeaders: 'includeLicenseHeaders',
  noticeFiles: 'includeNoticeFiles',
  packageLabels: 'includePackageLabels',
};

const dependencyDataToIncludeOptionEntries: Array<[DependencyDataKey, IncludeFilterOption]> =
  Object.entries(dependencyDataToIncludeOption) as Array<[DependencyDataKey, IncludeFilterOption]>;

function makeIncludeOption(key: IncludeFilterOption): SelectOption<IncludeFilterOption> {
  return selectOptionFactory(key, ReportOptionDetails[key]?.title ?? '');
}

function pushIncludeOption(
  includeKey: IncludeFilterOption,
  flatIncludeValues: Array<SelectOption<IncludeFilterOption>>,
  licenseIncludeValues: Array<SelectOption<IncludeFilterOption>>,
): void {
  const target = licenseIncludeOptions.has(includeKey) ? licenseIncludeValues : flatIncludeValues;
  if (!target.some((option) => option.value === includeKey)) {
    target.push(makeIncludeOption(includeKey));
  }
}

type FilterState = {
  flatIncludeValues: Array<SelectOption<IncludeFilterOption>>;
  licenseIncludeValues: Array<SelectOption<IncludeFilterOption>>;
  dependencyMetadataValues: SelectOption[];
  packageLabelExclusionValues: SelectOption[];
};

/**
 * Maps a saved PartialReportConfig to the option state used by the Options component.
 * Package label exclusions are stored as IDs in the config but the filter expects names,
 * so availablePackageLabels is used to hydrate the IDs into SelectOptions with names.
 */
export function mapReportConfigToOptionState(
  config: PartialReportConfig,
): FilterState {
  const flatIncludeValues: Array<SelectOption<IncludeFilterOption>> = [];
  const licenseIncludeValues: Array<SelectOption<IncludeFilterOption>> = [];
  const dependencyMetadataValues: SelectOption[] = [];

  // Map sections
  sectionToIncludeOptionEntries.forEach(([sectionKey, includeKey]) => {
    if (config.sections?.[sectionKey]) {
      pushIncludeOption(includeKey, flatIncludeValues, licenseIncludeValues);
    }
  });

  // Map toggles
  toggleToIncludeOptionEntries.forEach(([toggleKey, includeKey]) => {
    if (config.toggles?.[toggleKey]) {
      pushIncludeOption(includeKey, flatIncludeValues, licenseIncludeValues);
    }
  });

  // Map dependency data to metadata options
  dependencyDataToInfoOptionEntries.forEach(([dataKey, infoKey]) => {
    if (infoKey && config.dependencyData?.[dataKey]) {
      dependencyMetadataValues.push(selectOptionFactory(infoKey, DependencyInfoOptions[infoKey]));
    }
  });

  // Map dependency data keys that correspond to include options
  dependencyDataToIncludeOptionEntries.forEach(([dataKey, includeKey]) => {
    if (config.dependencyData?.[dataKey]) {
      pushIncludeOption(includeKey, flatIncludeValues, licenseIncludeValues);
    }
  });

  const packageLabelExclusionValues: SelectOption[] = (config.excludeFields?.packageLabels ?? [])
    .map((name) => selectOptionFactory(name));

  return { flatIncludeValues, licenseIncludeValues, dependencyMetadataValues, packageLabelExclusionValues };
}
